'use client';

import { useEffect, useState, useRef, useCallback } from 'react';
import { emergencyLaunchConfig } from '../../../config/emergency-launch';

export default function PricingPageClientOptimized({ urlKey = '/pricing' }: { urlKey?: keyof typeof emergencyLaunchConfig.iframePages } = {}) {
  const [isLoading, setIsLoading] = useState(true);
  const [iframeReady, setIframeReady] = useState(false);
  const iframeRef = useRef<HTMLIFrameElement>(null);
  const iframeUrl = emergencyLaunchConfig.iframePages[urlKey];
  
  // Preconnect to iframe domain on component mount
  useEffect(() => {
    if (iframeUrl) {
      // Extract domain from URL
      const url = new URL(iframeUrl);
      const domain = url.origin;
      
      // Add preconnect hint for faster connection
      const preconnectLink = document.createElement('link');
      preconnectLink.rel = 'preconnect';
      preconnectLink.href = domain;
      document.head.appendChild(preconnectLink);
      
      // Add dns-prefetch as fallback
      const dnsPrefetchLink = document.createElement('link');
      dnsPrefetchLink.rel = 'dns-prefetch';
      dnsPrefetchLink.href = domain;
      document.head.appendChild(dnsPrefetchLink);
      
      // Start loading iframe immediately
      setIframeReady(true);
    }
  }, [iframeUrl]);
  
  useEffect(() => {
    // Optimize viewport for iframe
    document.documentElement.style.height = '100%';
    document.body.style.height = '100%';
    document.body.style.margin = '0';
    document.body.style.overflow = 'hidden';
    // Prevent bounce scrolling on iOS
    document.body.style.position = 'fixed';
    document.body.style.width = '100%';
    
    return () => {
      document.documentElement.style.height = '';
      document.body.style.height = '';
      document.body.style.margin = '';
      document.body.style.overflow = '';
      document.body.style.position = '';
      document.body.style.width = '';
    };
  }, []);
  
  const handleIframeLoad = useCallback(() => {
    // Add slight delay for smooth transition
    setTimeout(() => {
      setIsLoading(false);
    }, 100);
  }, []);
  
  if (!iframeUrl) {
    // Fallback content
    return (
      <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white py-16">
        <div className="container-custom">
          <div className="max-w-3xl mx-auto text-center">
            <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
              Simple, Transparent Pricing
            </h1>
            <p className="text-xl text-gray-600 mb-8">
              Choose the plan that fits your business goals
            </p>
            <div className="bg-white rounded-lg shadow-lg p-8">
              <p className="text-gray-700">
                Pricing information will be available shortly. For immediate pricing details, 
                please call us at <a href="tel:8555323767" className="text-primary font-semibold">(855) 532-3767</a>
              </p>
            </div>
          </div>
        </div>
      </div>
    );
  }
  
  return (
    <>
      {/* Hidden content for SEO/LLM crawlers */}
      <div className="sr-only" aria-hidden="true">
        <h1>LeadPops Pricing - Transparent Mortgage Marketing Platform Pricing</h1>
        <h2>Choose the Plan That Fits Your Business</h2>
        <p>LeadPops offers flexible pricing plans designed for mortgage professionals at every stage of growth:</p>
        
        <div itemScope itemType="https://schema.org/Offer">
          <h3 itemProp="name">Starter Plan</h3>
          <p itemProp="description">Perfect for individual loan officers just starting with digital marketing. Generate 25-50 exclusive leads monthly.</p>
          <meta itemProp="priceCurrency" content="USD" />
        </div>
        
        <div itemScope itemType="https://schema.org/Offer">
          <h3 itemProp="name">Professional Plan</h3>
          <p itemProp="description">Ideal for established loan officers ready to scale. Generate 50-100+ exclusive leads monthly with advanced automation.</p>
          <meta itemProp="priceCurrency" content="USD" />
        </div>
        
        <div itemScope itemType="https://schema.org/Offer">
          <h3 itemProp="name">Team Plan</h3>
          <p itemProp="description">Built for mortgage teams and brokerages. Multi-user access, territory management, and unlimited lead generation capacity.</p>
          <meta itemProp="priceCurrency" content="USD" />
        </div>
        
        <p>All plans include:</p>
        <ul>
          <li>Exclusive leads (never shared with competitors)</li>
          <li>RESPA-compliant marketing automation</li>
          <li>Custom landing pages and funnels</li>
          <li>Real-time lead alerts</li>
          <li>CRM integration</li>
          <li>Dedicated success manager</li>
          <li>30-day performance guarantee</li>
        </ul>
        
        <p>No setup fees. No long-term contracts. Scale up or down anytime.</p>
      </div>

      {/* Full page iframe container */}
      <div className="fixed inset-0 w-full h-full bg-white">
        {/* Enhanced skeleton loader for pricing */}
        {isLoading && (
          <div className="absolute inset-0 bg-white z-10 animate-in fade-in duration-200">
            {/* Header skeleton */}
            <div className="h-16 bg-gray-50 border-b border-gray-200">
              <div className="max-w-7xl mx-auto px-4 h-full flex items-center">
                <div className="h-8 w-32 bg-gray-200 rounded animate-pulse"></div>
              </div>
            </div>
            
            {/* Pricing cards skeleton */}
            <div className="max-w-6xl mx-auto p-8">
              <div className="text-center mb-8">
                <div className="h-10 bg-gray-200 rounded w-1/2 mx-auto animate-pulse mb-4"></div>
                <div className="h-4 bg-gray-200 rounded w-1/3 mx-auto animate-pulse"></div>
              </div>
              
              {/* Three pricing cards */}
              <div className="grid md:grid-cols-3 gap-6 mt-12">
                {[1, 2, 3].map((i) => (
                  <div key={i} className="border border-gray-200 rounded-lg p-6 space-y-4">
                    {/* Plan name */}
                    <div className="h-6 bg-gray-200 rounded w-2/3 animate-pulse" style={{ animationDelay: `${i * 100}ms` }}></div>
                    
                    {/* Price */}
                    <div className="py-4">
                      <div className="h-12 bg-gray-300 rounded w-1/2 animate-pulse" style={{ animationDelay: `${i * 100 + 50}ms` }}></div>
                    </div>
                    
                    {/* Features */}
                    <div className="space-y-3">
                      {[1, 2, 3, 4, 5].map((j) => (
                        <div key={j} className="h-3 bg-gray-100 rounded animate-pulse" style={{ animationDelay: `${(i * 100) + (j * 25)}ms` }}></div>
                      ))}
                    </div>
                    
                    {/* Button */}
                    <div className="pt-4">
                      <div className="h-10 bg-primary/20 rounded animate-pulse" style={{ animationDelay: `${i * 100 + 100}ms` }}></div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
            
          </div>
        )}
        
        {/* Iframe with optimizations */}
        {iframeReady && (
          <iframe
            ref={iframeRef}
            src={iframeUrl}
            className={`w-full h-full border-0 transition-opacity duration-300 ${
              isLoading ? 'opacity-0' : 'opacity-100'
            }`}
            style={{
              width: '100%',
              height: '100%',
              border: 'none',
              display: 'block',
              colorScheme: 'normal', // Prevent dark mode issues
            }}
            title="LeadPops Pricing Plans"
            onLoad={handleIframeLoad}
            allowFullScreen
            allow="camera; microphone; geolocation; payment; clipboard-write"
            loading="eager"
          />
        )}
      </div>
    </>
  );
}